import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraInput } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useAuth } from '../../contexts/authContext';
import { RfiFollowUpsPanel } from './RfiFollowUpsPanel';
import { RfiSectionRow, sectionReadiness } from './RfiSectionRow';
import { useRfiPeople, useRfiSectionMutations, useRfiSections } from './rfiSectionHooks';
/** Flatten depth-first. The server orders each level by position; list order is not assumed. */ function flatten(sections) {
    const byParent = new Map();
    for (const s of sections){
        const list = byParent.get(s.parentSectionId) ?? [];
        list.push(s);
        byParent.set(s.parentSectionId, list);
    }
    for (const list of byParent.values())list.sort((a, b)=>a.position - b.position);
    const out = [];
    const walk = (parentId, depth)=>{
        const siblings = byParent.get(parentId) ?? [];
        siblings.forEach((section, index)=>{
            out.push({
                section,
                depth,
                siblings,
                index
            });
            walk(section.id, depth + 1);
        });
    };
    walk('', 0);
    return out;
}
/**
 * Manual outline + ownership + follow-ups for an RFI response (SAD-516 RFI-05).
 * Section readiness here is separate from the RFI's overall status.
 */ export function RfiOutlinePanel({ rfiResponseProjectId }) {
    const { user } = useAuth();
    const meId = user?.id ?? '';
    const { data: sections = [], isLoading, isError } = useRfiSections(rfiResponseProjectId);
    const { data: people = [] } = useRfiPeople();
    const { create, update, move, remove } = useRfiSectionMutations(rfiResponseProjectId);
    const [newTitle, setNewTitle] = useState('');
    const nodes = useMemo(()=>flatten(sections), [
        sections
    ]);
    const readyCount = nodes.filter((n)=>sectionReadiness(n.section).ready).length;
    const actions = {
        update: (a)=>update.mutate(a),
        move: (a)=>move.mutate(a),
        addChild: (parentSectionId)=>create.mutate({
                title: 'New subsection',
                parentSectionId
            }),
        remove: async (a)=>{
            try {
                return await remove.mutateAsync(a);
            } catch  {
                return null; // onError already toasted
            }
        }
    };
    const handleAdd = (e)=>{
        e.preventDefault();
        const title = newTitle.trim();
        if (!title) return;
        create.mutate({
            title
        }, {
            onSuccess: ()=>setNewTitle('')
        });
    };
    const moveTo = (sectionId, parentSectionId, position)=>actions.move({
            sectionId,
            parentSectionId,
            position
        });
    return /*#__PURE__*/ _jsxs("section", {
        className: "max-w-3xl space-y-3",
        "data-testid": Testids.rfiOutline.panel,
        "data-rfi-response-id": rfiResponseProjectId,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        className: "text-lg font-medium",
                        children: "Outline"
                    }),
                    /*#__PURE__*/ _jsxs(SpecraBadge, {
                        variant: nodes.length > 0 && readyCount === nodes.length ? 'success' : 'neutral',
                        "data-testid": Testids.rfiOutline.readinessSummary,
                        children: [
                            "Section readiness: ",
                            readyCount,
                            " of ",
                            nodes.length,
                            " ready"
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Section readiness is tracked per section and does not change the RFI's overall status."
            }),
            /*#__PURE__*/ _jsxs("form", {
                className: "flex items-center gap-2",
                onSubmit: handleAdd,
                children: [
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        className: "flex-1",
                        "aria-label": "New section title",
                        placeholder: "New section title",
                        value: newTitle,
                        onChange: (e)=>setNewTitle(e.target.value),
                        "data-testid": Testids.rfiOutline.newSectionInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "submit",
                        size: "sm",
                        variant: "secondary",
                        disabled: !newTitle.trim() || create.isPending,
                        iconLeft: /*#__PURE__*/ _jsx(Plus, {
                            className: "h-4 w-4"
                        }),
                        "data-testid": Testids.rfiOutline.addSectionButton,
                        children: "Add section"
                    })
                ]
            }),
            isLoading ? /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "Loading outline…"
            }) : isError ? /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-destructive",
                children: "Couldn't load the outline."
            }) : nodes.length === 0 ? /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "No sections yet."
            }) : /*#__PURE__*/ _jsx("ul", {
                className: "divide-y rounded-md border",
                children: nodes.map(({ section, depth, siblings, index })=>{
                    const parent = sections.find((s)=>s.id === section.parentSectionId);
                    return /*#__PURE__*/ _jsx(RfiSectionRow, {
                        section: section,
                        depth: depth,
                        people: people,
                        meId: meId,
                        actions: actions,
                        canMoveUp: index > 0,
                        canMoveDown: index < siblings.length - 1,
                        canIndent: index > 0,
                        canOutdent: Boolean(parent),
                        onMoveUp: ()=>moveTo(section.id, section.parentSectionId, index - 1),
                        onMoveDown: ()=>moveTo(section.id, section.parentSectionId, index + 1),
                        onIndent: ()=>moveTo(section.id, siblings[index - 1].id, -1),
                        onOutdent: ()=>{
                            if (!parent) return;
                            const parentSiblings = sections.filter((s)=>s.parentSectionId === parent.parentSectionId).sort((a, b)=>a.position - b.position);
                            const parentIndex = parentSiblings.findIndex((s)=>s.id === parent.id);
                            moveTo(section.id, parent.parentSectionId, parentIndex + 1);
                        }
                    }, section.id);
                })
            }),
            /*#__PURE__*/ _jsx(RfiFollowUpsPanel, {
                rfiResponseProjectId: rfiResponseProjectId,
                sections: nodes.map((n)=>n.section),
                people: people
            })
        ]
    });
}
